import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { BrowserRouter, Route, Switch } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import Home from './pages/Home';
import TopicComponent from './pages/Topic';

import styled, { createGlobalStyle } from 'styled-components';
import { PreciseCoordinates, TopicResponse } from '~/types';
import Confetti from './effects/Confetti';

import RotateMe from './components/RotateMe';
import PolicyScreen from './menu/PolicyScreen';
import GlassPanel from './components/GlassPanel';
import MainMenu from './menu/MainMenu';
import ProfileComponent from './menu/Profile';
import LanguageScreen from './menu/LanguageScreen';

import { Toaster } from 'react-hot-toast';
import DeleteProfileAYSScreen from './menu/profile/DeleteProfileAYSScreen';

import './i18n/i18n';
import LoadingTopicScreen from './components/LoadingTopicScreen';

import { AppContext } from '~/contexts/AppContext';

const GlobalStyle = createGlobalStyle`

    body, input, button {
        margin: 0;
        font-family: "Inter", sans-serif;
    }

    select, option {
        font-family: "Inter", sans-serif;
    }
    h1 {
        line-height: 1.3
    }
    h2  {
        line-height: 1.3;
    }
    h3 {
        font-size: 28px;
        font-weight: bold;
        line-height: 1.3;
    }
    
    p {
        line-height: 1.7;
        font-size: 22px;
        font-weight: 300;
        color: #333;
        b {
            font-weight: 999;
        }
    }
    a:active, a:focus {
        outline: 0;
        border: none;
        -moz-outline-style: none;
    }
`;

const BackgroundImage = styled.img`
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    z-index: -1;
    object-fit: cover;
    object-position: center;
    display: block;

    @media (orientation: portrait) {
        display: none;
    }
`;

const BackgroundImagePortrait = styled.img`
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    z-index: -1;
    object-fit: cover;
    object-position: center;
    display: none;

    @media (orientation: portrait) {
        display: block;
    }
`;

const AppContainer = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
`;

export type GenderData = 'male' | 'female' | 'other' | null;

export enum MenuScreen {
    None = 'none',
    MainMenu = 'main-menu',
    Language = 'language',
    Profile = 'profile',
    PrivacyNotice = 'privacy-notice',
    PrivacyNoticeMenu = 'privacy-notice-menu',
    PrivacyNoticeQna = 'privacy-notice-qna',
    Consent = 'consent',
    DeleteProfileAYS = 'delete-profile-ays'
}

export interface ProfileData {
    userId: string;
    locale: string;
    name: string;
    gender: GenderData;
    birthYear: number | null;
    cityName: string;
    regionName: string;
    countryName: string;
}

export type GeolocationConsent = 'granted' | 'denied' | 'prompt' | null;

interface AppState {
    loading: boolean;
    profile: ProfileData | null;
    topicData: TopicResponse | null;
    showResults: boolean;
    menuScreen: MenuScreen;
    geolocationConsent: GeolocationConsent;
    preciseCoordinates?: PreciseCoordinates;
    cookieConsent: 'needs' | 'granted' | 'create-cookie' | 'unknown';
    confettiActive: boolean;
    analyticsSessionId: string;
    languageSelectorVariant: 'new' | 'old';
}

const getApiURL = () => {
    if(import.meta.env.MODE === 'production') {
        return 'https://kazva.bg/api/v1';
    } else if(import.meta.env.MODE === 'development') {
        // this allows for connections on the local network
        return `http://${window.location.hostname}:8000/api/v1`;
    }
    
    return 'http://localhost:8000/api/v1';
};

const App: React.FC = () => {

    const apiUrl = getApiURL();

    const glassPanelRef = useRef<HTMLDivElement>(null);

    const chooseLanguageSelectorVariant = () => {
        const savedVariant = localStorage.getItem('languageSelectorVariant');
        if(savedVariant) {
            return savedVariant as 'new' | 'old';
        } else {
            const variant = Math.random() > 0.5 ? 'new' : 'old';
            localStorage.setItem('languageSelectorVariant', variant);
            return variant;
        }
    };

    const [state, setState] = useState<AppState>(() => ({
        loading: true,
        profile: null,
        topicData: null,
        showResults: false,
        menuScreen: MenuScreen.None,
        geolocationConsent: null,
        cookieConsent: 'unknown',
        confettiActive: false,
        analyticsSessionId: uuidv4(),
        languageSelectorVariant: chooseLanguageSelectorVariant()
    }));
    const stateRef = useRef<AppState>(state);

    const setTopicData = (newTopicData: TopicResponse | null) => {

        if(!newTopicData) {
            stateRef.current = { ...stateRef.current, topicData: null, showResults: false, loading: false };
            setState(stateRef.current);
            return;
        }

        const showResults: boolean | undefined =
            newTopicData.topic.pvtShowPeopleStats ||
            newTopicData.topic.pvtShowGenderStats ||
            newTopicData.topic.pvtShowAgeStats ||
            newTopicData.topic.pvtShowGenderByAgeStats;

        stateRef.current = { ...stateRef.current, topicData: newTopicData, showResults: !!showResults, loading: true };
        setState(stateRef.current);
    };

    const updatePreciseLocation = () => new Promise<void>((resolve, reject) => {

        if(!navigator.geolocation) {
            console.error('Geolocation is not supported by this browser.');
            resolve();
            return;
        }

        navigator.geolocation.getCurrentPosition((position) => {
                
            setPreciseCoordinates(position.coords);
            if(state.geolocationConsent !== 'granted')
                setGeolocationConsent('granted');
            resolve();

        }, (error) => { reject(error) }, {
            enableHighAccuracy: true, // Request precise location
            timeout: 5000, // Wait up to 5 seconds
            maximumAge: 0, // Do not use cached location
        });
    });

    useEffect(() => {
        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: 'geolocation' }).then(permissionStatus => {

                let geolocationConsent: 'granted' | 'denied' | 'prompt';

                if (permissionStatus.state === 'granted') {
                    geolocationConsent = 'granted';
                    updatePreciseLocation();
                } else if (permissionStatus.state === 'denied') {
                    geolocationConsent = 'denied';
                } else {
                    geolocationConsent = 'prompt';
                }

                stateRef.current = {...stateRef.current, geolocationConsent }
                setState(stateRef.current);
            });
        }
    }, []);

    /*useEffect(() => {
        console.log('Geolocation consent changed to', state.geolocationConsent);
        //if(state.geolocationConsent === 'granted' && !state.preciseCoordinates) {

        //}
    }, [state.geolocationConsent]);*/

    /*useEffect(() => {
        console.log('Precise coordinates set to', state.preciseCoordinates);
    }, [state.preciseCoordinates]);*/

    const setGeolocationConsent = (geolocationConsent: GeolocationConsent) => {
        stateRef.current = {...stateRef.current, geolocationConsent }
        setState(stateRef.current);
    };

    const setPreciseCoordinates = (coords: GeolocationCoordinates) => {
        stateRef.current = {...stateRef.current, preciseCoordinates: {
            acc: coords.accuracy,
            alt: coords.altitude,
            altAcc: coords.altitudeAccuracy,
            lat: coords.latitude,
            lon: coords.longitude,
        }};
        setState(stateRef.current);
    };

    const authenticateUser = (profile: ProfileData) => {
        stateRef.current = { ...stateRef.current, profile, cookieConsent: 'granted' };
        setState(stateRef.current);
        window._paq.push(['setUserId', profile.userId]);
    }

    useEffect(() => {
        if(!state.profile) {
            if(state.cookieConsent === 'granted') {
                axios.get(`${apiUrl}/user`).then(res => {
                    authenticateUser(res.data.profile);
                }); 
            } else if(state.cookieConsent === 'create-cookie') {
                axios.post(`${apiUrl}/user`).then(res => {
                    authenticateUser(res.data.profile);
                });
            }
        }
    }, [state.cookieConsent]);

    const setGlassPanelColor = (color: string) => {
        if (glassPanelRef.current) {
            glassPanelRef.current.style.backgroundColor = color;
        }
    };

    const setGlassPanelBackdropFilter = (blur: number, saturate: number, brightness: number) => {
        if (glassPanelRef.current) {
            const styleObject = glassPanelRef.current.style as any;
            styleObject.backdropFilter = `blur(${blur}px) saturate(${saturate}) brightness(${brightness})`;
            styleObject['-webkit-backdrop-filter'] = `blur(${blur}px) saturate(${saturate}) brightness(${brightness})`;
        }
    };

    const renderMenuScreen = () => {
        switch (state.menuScreen) {
            case MenuScreen.None:
                return null;
            case MenuScreen.PrivacyNotice:
                return <PolicyScreen backClicked={() => setMenuScreen(MenuScreen.Consent)} />;
            case MenuScreen.PrivacyNoticeMenu:
                return <PolicyScreen backClicked={() => setMenuScreen(MenuScreen.MainMenu)} />;
            case MenuScreen.PrivacyNoticeQna:
                return <PolicyScreen backClicked={() => setMenuScreen(MenuScreen.None)} />;
            case MenuScreen.MainMenu:
                return <MainMenu />;
            case MenuScreen.Profile:
                return <ProfileComponent />;
            case MenuScreen.Language:
                return <LanguageScreen />
            case MenuScreen.DeleteProfileAYS:
                return <DeleteProfileAYSScreen />

            default: return null;
        }
    };

    const setProfile = (profile: ProfileData | null) => {
        stateRef.current = { ...stateRef.current, profile };
        setState(stateRef.current);
    };

    const setLoading = (loading: boolean) => {
        stateRef.current = { ...stateRef.current, loading };
        setState(stateRef.current);
    };

    const setMenuScreen = (menuScreen: MenuScreen) => {
        stateRef.current = { ...stateRef.current, menuScreen };
        setState(stateRef.current);
    };

    const getBackgroundImage = () => {
        if (!state.topicData)
            return '';

        if (state.topicData.topic.bgUrl)
            return state.topicData.topic.bgUrl;

        return '/images/GradientTexture-Default.jpg';
    };

    const getBackgroundImagePortrait = () => {
        if (!state.topicData)
            return '';

        if (state.topicData.topic.bgPortraitUrl)
            return state.topicData.topic.bgPortraitUrl;

        if (state.topicData.topic.bgUrl)
            return state.topicData.topic.bgUrl;

        return '/images/GradientTexture-Default.jpg';
    };

    const revertSliderRef = useRef<() => void>(() => undefined);

    const setRevertSlider = (revertSlider: () => void) => {
        revertSliderRef.current = revertSlider;
    };

    const triggerConfetti = () => {
        stateRef.current = { ...stateRef.current, confettiActive: true };
        setState(stateRef.current);

        setTimeout(() => {
            stateRef.current = { ...stateRef.current, confettiActive: false };
            setState(stateRef.current);
        }, 5000);
    };

    const backgroundImage = useMemo(() => {
        return getBackgroundImage();
    }, [state.topicData]);

    const backgroundImagePortrait = useMemo(() => {
        return getBackgroundImagePortrait();
    }, [state.topicData]);

    const recordAnalyticsEvent = async (
        eventCategory: string,
        eventName: string,
        topicId?: string,
        entityId?: number
    ) => {
        await axios.post(`${apiUrl}/analytics/event`, {
            eventCategory,
            eventName,
            sessionId: state.analyticsSessionId,
            topicId,
            entityId
        });
    };

    return (
        <AppContext.Provider value={{
            apiUrl,
            profile: state.profile,
            setProfile,
            topicData: state.topicData, setTopicData,
            showResults: state.showResults,
            menuScreen: state.menuScreen, setMenuScreen,
            setGlassPanelColor, setGlassPanelBackdropFilter,
            setLoading,
            geolocationConsent: state.geolocationConsent,
            setGeolocationConsent,
            preciseCoordinates: state.preciseCoordinates,
            updatePreciseLocation,
            revertSlider: revertSliderRef.current,
            setRevertSlider,
            triggerConfetti,
            recordAnalyticsEvent,
            languageSelectorVariant: state.languageSelectorVariant
        }}>
            <GlobalStyle />
            <RotateMe />
            <Toaster />
            <Confetti isActive={state.confettiActive} />
            <AppContainer>
                {/*<BackgroundImage src={state.topic?.ti ? state.topic.ti : '/images/gradient-backgrounds/GradientTextures-04.jpg'} />*/}
                <GlassPanel style={{ backgroundColor: '#000' }} ref={glassPanelRef}>
                    {renderMenuScreen()}
                    <div style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        display: state.menuScreen === MenuScreen.None ? 'block' : 'none'
                    }}>
                        <BrowserRouter>
                            <Switch>
                                <Route path='/:topicHash' component={TopicComponent} />
                                <Route exact path='/' component={Home} />
                            </Switch>
                        </BrowserRouter>
                    </div>
                </GlassPanel>
                <LoadingTopicScreen isLoading={state.loading} />
                {backgroundImage && <BackgroundImage
                    onLoad={() => { if (stateRef.current.loading) setLoading(false) }}
                    src={backgroundImage}
                />}
                {backgroundImagePortrait && <BackgroundImagePortrait
                    onLoad={() => { if (stateRef.current.loading) setLoading(false) }}
                    src={backgroundImagePortrait}
                />}
            </AppContainer>
        </AppContext.Provider >
    );
};

export default App;